import React, { useState, useEffect } from 'react';
import eventosGanttService from '../services/eventosGanttService';
import { toast } from 'react-toastify';
import './StockValidacionWidget.css';

import Loading from './Loading';
/**
 * Widget para mostrar la validación de stock de recursos
 * Muestra recursos que cumplen y no cumplen con el stock necesario
 */
const StockValidacionWidget = () => {
  const [loading, setLoading] = useState(true);
  const [reporte, setReporte] = useState(null);
  const [mostrarTodos, setMostrarTodos] = useState(false);

  useEffect(() => {
    cargarValidacionStock();
  }, []);

  const cargarValidacionStock = async () => {
    setLoading(true);
    try {
      const response = await eventosGanttService.tareas.validarStockRecursos();
      setReporte(response.data);
    } catch (error) {
      console.error('Error al cargar validación de stock:', error);
      toast.error('Error al cargar validación de stock');
    } finally {
      setLoading(false);
    }
  };

  const formatFecha = (fecha) => {
    const date = new Date(fecha);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const agruparTareasPorProyectoYActividad = (tareas) => {
    const grupos = {};

    tareas.forEach(tarea => {
      const eventoId = tarea.evento_id;
      const actividadId = tarea.actividad_id;

      if (!grupos[eventoId]) {
        grupos[eventoId] = {
          evento_id: eventoId,
          evento_titulo: tarea.evento_titulo,
          actividades: {}
        };
      }

      if (!grupos[eventoId].actividades[actividadId]) {
        grupos[eventoId].actividades[actividadId] = {
          actividad_id: actividadId,
          actividad_titulo: tarea.actividad_titulo,
          tareas: []
        };
      }

      grupos[eventoId].actividades[actividadId].tareas.push(tarea);
    });

    return grupos;
  };

  if (loading) {
    return (
      <div className="stock-validacion-widget">
        <div className="widget-header">
          <h2>📦 Validación de Stock (próximos 15 días)</h2>
        </div>
        <Loading fullScreen={false} size="sm" mensaje="Cargando validación de stock" />
      </div>
    );
  }

  if (!reporte || reporte.total_recursos_con_stock === 0) {
    return (
      <div className="stock-validacion-widget">
        <div className="widget-header">
          <h2>📦 Validación de Stock (próximos 15 días)</h2>
        </div>
        <div className="widget-empty">
          <p>No hay recursos con control de stock en tareas próximas</p>
        </div>
      </div>
    );
  }

  const recursosFiltrados = mostrarTodos
    ? reporte.recursos
    : reporte.recursos.filter(r => r.requiere_solicitud);

  // Debug: verificar datos
  if (recursosFiltrados.length > 0) {
    console.log('Recurso ejemplo:', recursosFiltrados[0]);
  }

  return (
    <div className="stock-validacion-widget">
      <div className="widget-header">
        <div>
          <h2>📦 Validación de Stock</h2>
          <p className="fecha-rango">
            {formatFecha(reporte.fecha_inicio)} - {formatFecha(reporte.fecha_fin)}
          </p>
        </div>
        <button
          className="btn-refresh"
          onClick={cargarValidacionStock}
          title="Actualizar validación"
        >
          🔄
        </button>
      </div>

      {/* Resumen */}


      {/* Toggle para mostrar todos o solo los que requieren solicitud */}
      <div className="filter-toggle">
        <label>
          <input
            type="checkbox"
            checked={mostrarTodos}
            onChange={(e) => setMostrarTodos(e.target.checked)}
          />
          <span>Mostrar todos los recursos</span>
        </label>
      </div>

      {/* Lista de recursos */}
      <div className="recursos-list">
        {recursosFiltrados.length === 0 ? (
          <div className="recursos-empty">
            <p>✅ Todos los recursos tienen stock suficiente</p>
          </div>
        ) : (
          recursosFiltrados.map((recurso) => (
            <div
              key={recurso.recurso_id}
              className={`recurso-card ${recurso.requiere_solicitud ? 'requiere-solicitud' : 'stock-ok'}`}
            >
              <div className="recurso-header">
                <div className="recurso-info">
                  <h3>{recurso.recurso_nombre}</h3>
                  {recurso.recurso_descripcion && (
                    <p className="recurso-descripcion">{recurso.recurso_descripcion}</p>
                  )}
                </div>
                <div className="recurso-status-container">
                  <div className={`recurso-status ${recurso.requiere_solicitud ? 'requiere' : 'ok'}`}>
                    {recurso.requiere_solicitud ? '⚠️ Requiere Solicitud' : '✅ Stock OK'}
                  </div>
                  {(recurso.dias_hasta_primera_tarea !== null && recurso.dias_hasta_primera_tarea !== undefined) && (
                    <div className="dias-faltantes">
                      {Number(recurso.dias_hasta_primera_tarea) === 0 ? (
                        <span className="dias-urgente">🔴 Hoy</span>
                      ) : Number(recurso.dias_hasta_primera_tarea) === 1 ? (
                        <span className="dias-urgente">🔴 Mañana</span>
                      ) : Number(recurso.dias_hasta_primera_tarea) <= 3 ? (
                        <span className="dias-urgente">⚠️ {Number(recurso.dias_hasta_primera_tarea)} día{Number(recurso.dias_hasta_primera_tarea) !== 1 ? 's' : ''}</span>
                      ) : (
                        <span className="dias-normal">📅 {Number(recurso.dias_hasta_primera_tarea)} días</span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="recurso-cantidades">
                <div className="cantidad-item">
                  <span className="cantidad-label">Requerida</span>
                  <span className="cantidad-value">{parseFloat(recurso.cantidad_requerida).toFixed(2)}</span>
                </div>
                <div className="cantidad-item">
                  <span className="cantidad-label">Solicitada</span>
                  <span className="cantidad-value">{parseFloat(recurso.cantidad_solicitada).toFixed(2)}</span>
                </div>
                <div className="cantidad-item pendiente">
                  <span className="cantidad-label">Pendiente</span>
                  <span className="cantidad-value">{parseFloat(recurso.cantidad_pendiente).toFixed(2)}</span>
                </div>
                <div className="cantidad-item stock">
                  <span className="cantidad-label">Stock Total</span>
                  <span className="cantidad-value">{parseFloat(recurso.stock_disponible_total).toFixed(2)}</span>
                </div>
                {recurso.requiere_solicitud && (
                  <div className="cantidad-item solicitar">
                    <span className="cantidad-label">A Solicitar</span>
                    <span className="cantidad-value destacado">{parseFloat(recurso.cantidad_a_solicitar).toFixed(2)}</span>
                  </div>
                )}
              </div>

              {/* Stock por bodega */}
              {recurso.stock_por_bodega.length > 0 && (
                <div className="bodegas-section">
                  <h4>Stock por Bodega</h4>
                  <div className="bodegas-list">
                    {recurso.stock_por_bodega.map((bodega) => (
                      <div key={bodega.bodega_id} className="bodega-item">
                        <span className="bodega-nombre">{bodega.bodega_nombre}</span>
                        <div className="bodega-info">
                          <span className="bodega-stock">
                            {parseFloat(bodega.cantidad_disponible).toFixed(2)} un.
                            {bodega.requiere_reposicion && (
                              <span className="reposicion-tag" title="Bajo stock mínimo">⚠️</span>
                            )}
                          </span>
                          <span className="bodega-ppp">
                            PPP: ${parseFloat(bodega.precio_costo_unitario).toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tareas asociadas */}
              {recurso.tareas_asociadas.length > 0 && (
                <details className="tareas-section">
                  <summary>
                    Tareas asociadas ({recurso.tareas_asociadas.length})
                  </summary>
                  <div className="tareas-agrupadas">
                    {Object.values(agruparTareasPorProyectoYActividad(recurso.tareas_asociadas)).map((proyecto) => (
                      <div key={proyecto.evento_id} className="proyecto-group">
                        <div className="proyecto-header">
                          <span className="proyecto-icon">📁</span>
                          <span className="proyecto-nombre">{proyecto.evento_titulo}</span>
                        </div>
                        {Object.values(proyecto.actividades).map((actividad) => (
                          <div key={actividad.actividad_id} className="actividad-group">
                            <div className="actividad-header">
                              <span className="actividad-icon">📋</span>
                              <span className="actividad-nombre">{actividad.actividad_titulo}</span>
                            </div>
                            <div className="tareas-list">
                              {actividad.tareas.map((tarea) => (
                                <div key={tarea.tarea_id} className="tarea-item">
                                  <span className="tarea-titulo">{tarea.tarea_titulo}</span>
                                  <span className="tarea-fechas">
                                    {formatFecha(tarea.fecha_inicio)} - {formatFecha(tarea.fecha_termino)}
                                  </span>
                                  <span className="tarea-cantidad">
                                    Req: {parseFloat(tarea.cantidad_presupuesto).toFixed(2)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default StockValidacionWidget;
